import { useState, ChangeEventHandler } from 'react'
import { DayPicker, DateRange, SelectRangeEventHandler } from 'react-day-picker'
import { isAfter, isBefore, isValid, isWeekend, startOfToday, parseISO } from 'date-fns/esm'
import { useFormContext } from 'react-hook-form'
import { ErrorMessage } from '@hookform/error-message'

import { isHoliday } from '@/main/app/utils/holidays'

import { formatLongDate, isValidDateStr, isWorkday, toDateStr } from '@/main/app/utils/dates'
import { DatesAndShipping } from '@/main/app/schema'
import { inputClassList, labelClassList } from '@/main/app/utils/classNames'

import 'react-day-picker/dist/style.css'
import './date-picker.css'

function isValidRentalDate(date: Date) {
  return isValid(date) && isWorkday(date) && !isBefore(date, startOfToday())
}

function resolveDateErrorMessage(date: Date) {
  if (isWeekend(date)) {
    return 'Please select a weekday (ACME is closed on weekends)'
  }

  if (isHoliday(date)) {
    return 'Please select a different day (ACME is closed for holidays)'
  }

  if (isBefore(date, startOfToday())) {
    return 'Please select a day on or after today'
  }

  return 'Date is invalid'
}

export function RentalDatePicker() {
  const {
    register,
    getValues,
    setValue,
    resetField,
    setError,
    formState: { errors },
  } = useFormContext<DatesAndShipping>()

  const defaultStart = getValues('dates.startDate')
  const defaultEnd = getValues('dates.endDate')

  const parseStart = defaultStart && parseISO(defaultStart)
  const parseEnd = defaultEnd && parseISO(defaultEnd)

  const defaultSelected: DateRange = {
    from: parseStart && !isBefore(parseStart, startOfToday()) ? parseStart : undefined,
    to: parseEnd && !isBefore(parseEnd, startOfToday()) ? parseEnd : undefined,
  }

  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(defaultSelected)

  const handleFromBlur: ChangeEventHandler<HTMLInputElement> = (e) => {
    const input = e.target.value

    if (!input) {
      resetField('dates.startDate', { defaultValue: '' })
      setSelectedRange({ from: undefined, to: selectedRange?.to })

      return
    }

    if (isValidDateStr(input)) {
      const date = parseISO(input)

      if (!isValidRentalDate(date)) {
        resetField('dates.startDate')
        setSelectedRange({ from: undefined, to: selectedRange?.to })
        setError('dates.startDate', { message: resolveDateErrorMessage(date) })

        return
      }

      if (selectedRange?.to && isAfter(date, selectedRange.to)) {
        setSelectedRange({ from: date, to: undefined })
        resetField('dates.endDate')
      } else {
        setSelectedRange({ from: date, to: selectedRange?.to })
      }

      setValue('dates.startDate', toDateStr(date), { shouldValidate: true })
    } else {
      resetField('dates.startDate')
      setError('dates.startDate', { message: `"${input}" is not a valid date/date format` })

      return
    }
  }

  const handleToBlur: ChangeEventHandler<HTMLInputElement> = (e) => {
    const input = e.target.value

    if (!input) {
      resetField('dates.endDate', { defaultValue: '' })
      setSelectedRange({ from: selectedRange?.from, to: undefined })

      return
    }

    if (isValidDateStr(input)) {
      const date = parseISO(input)

      if (!isValidRentalDate(date)) {
        resetField('dates.endDate', { defaultValue: '' })
        setSelectedRange({ from: undefined, to: selectedRange?.to })
        setError('dates.endDate', { message: resolveDateErrorMessage(date) })

        return
      }

      if (selectedRange?.from && isBefore(date, selectedRange.from)) {
        setSelectedRange({ from: undefined, to: date })
        resetField('dates.startDate', { defaultValue: '' })
      } else {
        setSelectedRange({ from: selectedRange?.from, to: date })
      }

      setValue('dates.endDate', toDateStr(date), { shouldValidate: true })
    } else {
      resetField('dates.endDate', { defaultValue: '' })
      setError('dates.endDate', { message: `${input} is not a valid date format` })

      return
    }
  }

  const handleRangeSelect: SelectRangeEventHandler = (range: DateRange | undefined) => {
    if (range?.from && !isValidRentalDate(range.from)) return
    if (range?.to && !isValidRentalDate(range.to)) return

    setSelectedRange(range)

    if (range?.from) {
      setValue('dates.startDate', toDateStr(range.from), { shouldValidate: true })
    } else {
      resetField('dates.startDate', { defaultValue: '' })
    }
    if (range?.to) {
      setValue('dates.endDate', toDateStr(range.to), { shouldValidate: true })
    } else {
      resetField('dates.endDate', { defaultValue: '' })
    }
  }

  return (
    <div className="twc-flex twc-flex-col twc-gap-2 sm:twc-gap-4 twc-items-center twc-justify-around sm:twc-justify-between sm:twc-flex-row sm:twc-flex-wrap md:twc-pb-4">
      <div className="twc-date-picker twc-not-prose twc-mx-auto twc-flex-grow-0 2xl:twc-px-4 sm:twc-basis-52">
        <DayPicker
          mode="range"
          defaultMonth={defaultStart ? parseISO(defaultStart) : new Date()}
          fromDate={new Date()}
          toYear={new Date().getFullYear() + 1}
          disabled={(day: Date) => !isWorkday(day) || isBefore(day, startOfToday())}
          selected={selectedRange}
          onSelect={handleRangeSelect}
          modifiers={{ holiday: (day: Date) => isHoliday(day) }}
          modifiersClassNames={{ holiday: 'day-is-holiday' }}
          footer={
            <dl className="twc-text-sm twc-mx-auto twc-mt-3">
              <div className="twc-flex twc-justify-between">
                <dt className="twc-text-slate-600">Start:</dt>
                <dd className="twc-font-medium">
                  {selectedRange?.from ? (
                    formatLongDate(selectedRange.from)
                  ) : (
                    <em className="twc-font-light">Choose your start date</em>
                  )}
                </dd>
              </div>
              <div className="twc-flex twc-justify-between">
                <dt className="twc-text-slate-600">Return:</dt>
                <dd className="twc-font-medium">
                  {selectedRange?.to ? (
                    formatLongDate(selectedRange.to)
                  ) : (
                    <em className="twc-font-light">Choose your return date</em>
                  )}
                </dd>
              </div>
            </dl>
          }
          fixedWeeks
        />
      </div>

      <div className="twc-flex twc-flex-grow sm:twc-basis-56 sm:twc-mx-auto twc-my-8 twc-flex-col twc-items-center twc-justify-center twc-gap-2 pb-12 twc-leading-tight">
        <div>
          <label className={labelClassList} htmlFor="startDate">
            Start Date
          </label>

          <input
            id="startDate"
            type="text"
            pattern="[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}"
            title="Enter date as 4-digit year, 2-digit month, and 2-digit day"
            size={10}
            placeholder="YYYY-MM-DD"
            className={`${inputClassList} md:twc-max-w-sm twc-mt-2`}
            aria-invalid={errors.dates?.startDate ? 'true' : 'false'}
            aria-describedby="start-desc"
            {...register('dates.startDate', {
              onBlur: handleFromBlur,
              required: true,
            })}
          />

          <p id="start-desc" className="twc-my-0 twc-text-zinc-500/80 twc-prose-sm">
            Select or enter a day to begin your rental
          </p>

          <div id="dates.startDate-feedback" aria-live="polite" className="twc-h-10">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-text-sm twc-text-red-600"
              errors={errors}
              name="dates.startDate"
              as="p"
            />
          </div>
        </div>

        <div>
          <label className={labelClassList} htmlFor="endDate">
            Return Date
          </label>

          <input
            id="endDate"
            type="text"
            pattern="[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}"
            title="Enter date as 4-digit year, 2-digit month, and 2-digit day"
            size={10}
            placeholder="YYYY-MM-DD"
            className={`${inputClassList} md:twc-max-w-sm twc-mt-2`}
            aria-invalid={errors.dates?.endDate ? 'true' : 'false'}
            aria-describedby="end-desc"
            {...register('dates.endDate', {
              onBlur: handleToBlur,
              required: true,
            })}
          />

          <p id="end-desc" className="twc-my-0 twc-text-zinc-500/80 twc-prose-sm">
            Select or enter a day to end your rental
          </p>

          <div id="dates.endDate-feedback" aria-live="polite">
            <ErrorMessage
              className="twc-mt-1 twc-mb-0 twc-text-sm twc-text-red-600"
              errors={errors}
              name="dates.endDate"
              as="p"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
